/**
 * Customer Form Component
 * Step 3 of the booking widget - Enter customer and vehicle info
 */

import { PhoneInput } from '../ui/PhoneInput';
import { DNIInput } from '../ui/DNIInput';
import { type CountryCode } from '@leal-crm/shared/utils/phoneUtils';

interface CustomerFormData {
  caseCategory: 'particular' | 'seguro' | 'garantia';
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceClaimNumber?: string;
  insuranceAdjusterName?: string;
  insuranceAdjusterPhone?: string;
  insuranceAdjusterEmail?: string;
  name: string;
  email: string;
  phone: string;
  countryCode: CountryCode;
  dni?: string;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear?: number;
  notes?: string;
  gdprConsent: boolean;
  marketingConsent: boolean;
}

interface CustomerFormProps {
  data: CustomerFormData;
  onChange: (data: CustomerFormData) => void;
  errors: Partial<Record<keyof CustomerFormData, string>>;
  language?: 'es' | 'en';
  stage?: 'contact' | 'vehicle' | 'all';
  selectedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
}

export function CustomerForm({ data, onChange, errors, language = 'es', stage = 'all', selectedFiles = [], onFilesChange }: CustomerFormProps) {
  const handleChange = (field: keyof CustomerFormData, value: string | boolean | number | undefined) => {
    onChange({ ...data, [field]: value });
  };

  const t = language === 'es' ? translations.es : translations.en;

  const showContact = stage === 'contact' || stage === 'all';
  const showVehicle = stage === 'vehicle' || stage === 'all';

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900">
        {stage === 'contact' ? t.customerInfo : stage === 'vehicle' ? t.vehicleInfo : t.title}
      </h3>

      {/* Case category - show on contact stage */}
      {showContact && (
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-gray-700">{t.caseCategoryTitle}</h4>

        <div className="grid gap-2 sm:grid-cols-3">
          {([
            { id: 'particular', label: t.caseParticular, description: t.caseParticularDesc },
            { id: 'seguro', label: t.caseInsurance, description: t.caseInsuranceDesc },
            { id: 'garantia', label: t.caseWarranty, description: t.caseWarrantyDesc },
          ] as const).map((opt) => (
            <label
              key={opt.id}
              className={`cursor-pointer rounded-md border p-2 transition-colors hover:bg-gray-50 ${
                data.caseCategory === opt.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="caseCategory"
                  value={opt.id}
                  checked={data.caseCategory === opt.id}
                  onChange={() => handleChange('caseCategory', opt.id)}
                  className="mt-0.5 h-3.5 w-3.5 text-blue-500 border-gray-300 focus:ring-blue-500"
                />
                <div>
                  <div className="text-xs font-medium text-gray-900">{opt.label}</div>
                  <div className="text-[10px] text-gray-500 leading-tight">{opt.description}</div>
                </div>
              </div>
            </label>
          ))}
        </div>

        {data.caseCategory === 'seguro' && (
          <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
            <h5 className="text-xs font-medium text-gray-700 mb-2">{t.insuranceTitle}</h5>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">{t.insuranceCompany}</label>
                <input
                  type="text"
                  value={data.insuranceCompany || ''}
                  onChange={(e) => handleChange('insuranceCompany', e.target.value)}
                  className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.insuranceCompany ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t.insuranceCompanyPlaceholder}
                />
                {errors.insuranceCompany && <p className="text-red-500 text-[10px] mt-0.5">{errors.insuranceCompany}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t.insurancePolicyNumber} <span className="text-gray-400 text-[10px]">({t.optional})</span>
                </label>
                <input
                  type="text"
                  value={data.insurancePolicyNumber || ''}
                  onChange={(e) => handleChange('insurancePolicyNumber', e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t.insurancePolicyNumberPlaceholder}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t.insuranceClaimNumber}</label>
                <input
                  type="text"
                  value={data.insuranceClaimNumber || ''}
                  onChange={(e) => handleChange('insuranceClaimNumber', e.target.value)}
                  className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.insuranceClaimNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t.insuranceClaimNumberPlaceholder}
                />
                {errors.insuranceClaimNumber && (
                  <p className="text-red-500 text-[10px] mt-0.5">{errors.insuranceClaimNumber}</p>
                )}
              </div>

              <div className="sm:col-span-2 mt-1">
                <h6 className="text-[10px] font-medium text-gray-500 mb-1.5">{t.adjusterTitle}</h6>
                <div className="grid gap-2 sm:grid-cols-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t.adjusterName} <span className="text-gray-400 text-[10px]">({t.optional})</span>
                    </label>
                    <input
                      type="text"
                      value={data.insuranceAdjusterName || ''}
                      onChange={(e) => handleChange('insuranceAdjusterName', e.target.value)}
                      className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.insuranceAdjusterName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t.adjusterNamePlaceholder}
                    />
                    {errors.insuranceAdjusterName && (
                      <p className="text-red-500 text-[10px] mt-0.5">{errors.insuranceAdjusterName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t.adjusterPhone} <span className="text-gray-400 text-[10px]">({t.optional})</span>
                    </label>
                    <input
                      type="tel"
                      value={data.insuranceAdjusterPhone || ''}
                      onChange={(e) => handleChange('insuranceAdjusterPhone', e.target.value)}
                      className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.insuranceAdjusterPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t.adjusterPhonePlaceholder}
                    />
                    {errors.insuranceAdjusterPhone && (
                      <p className="text-red-500 text-[10px] mt-0.5">{errors.insuranceAdjusterPhone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t.adjusterEmail} <span className="text-gray-400 text-[10px]">({t.optional})</span>
                    </label>
                    <input
                      type="email"
                      value={data.insuranceAdjusterEmail || ''}
                      onChange={(e) => handleChange('insuranceAdjusterEmail', e.target.value)}
                      className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.insuranceAdjusterEmail ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t.adjusterEmailPlaceholder}
                    />
                    {errors.insuranceAdjusterEmail && (
                      <p className="text-red-500 text-[10px] mt-0.5">{errors.insuranceAdjusterEmail}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Customer info - show on contact stage */}
      {showContact && (
      <div className="space-y-3">
        {stage === 'all' && <h4 className="text-xs font-medium text-gray-700">{t.customerInfo}</h4>}

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t.name}</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t.namePlaceholder}
            />
            {errors.name && <p className="text-red-500 text-[10px] mt-0.5">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t.email}</label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t.emailPlaceholder}
            />
            {errors.email && <p className="text-red-500 text-[10px] mt-0.5">{errors.email}</p>}
          </div>

          <div>
            <PhoneInput
              value={data.phone}
              countryCode={data.countryCode}
              onValueChange={(phone) => handleChange('phone', phone)}
              onCountryChange={(countryCode) => handleChange('countryCode', countryCode)}
              label={t.phone}
              error={errors.phone}
              placeholder={t.phonePlaceholder}
              autoDetectCountry={!data.phone}
            />
          </div>

          <div>
            <DNIInput
              value={data.dni || ''}
              onValueChange={(dni) => handleChange('dni', dni)}
              label={t.dni}
              error={errors.dni}
              placeholder="12345678A"
              showValidationType={true}
            />
          </div>
        </div>
      </div>
      )}

      {/* Vehicle info - show on vehicle stage */}
      {showVehicle && (
      <>
      <div className="space-y-3">
        {stage === 'all' && <h4 className="text-xs font-medium text-gray-700">{t.vehicleInfo}</h4>}

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t.registration}</label>
            <input
              type="text"
              value={data.vehicleRegistration}
              onChange={(e) => handleChange('vehicleRegistration', e.target.value.toUpperCase())}
              className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                errors.vehicleRegistration ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t.registrationPlaceholder}
            />
            {errors.vehicleRegistration && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.vehicleRegistration}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t.year} <span className="text-gray-400 text-[10px]">({t.optional})</span>
            </label>
            <input
              type="number"
              value={data.vehicleYear || ''}
              onChange={(e) =>
                handleChange('vehicleYear', e.target.value ? parseInt(e.target.value) : undefined)
              }
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="2020"
              min="1900"
              max={new Date().getFullYear() + 1}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t.make}</label>
            <input
              type="text"
              value={data.vehicleMake}
              onChange={(e) => handleChange('vehicleMake', e.target.value)}
              className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                errors.vehicleMake ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t.makePlaceholder}
            />
            {errors.vehicleMake && <p className="text-red-500 text-[10px] mt-0.5">{errors.vehicleMake}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t.model}</label>
            <input
              type="text"
              value={data.vehicleModel}
              onChange={(e) => handleChange('vehicleModel', e.target.value)}
              className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                errors.vehicleModel ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t.modelPlaceholder}
            />
            {errors.vehicleModel && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.vehicleModel}</p>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t.notes} <span className="text-gray-400 text-[10px]">({t.optional})</span>
        </label>
        <textarea
          value={data.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder={t.notesPlaceholder}
        />
      </div>

      {/* Photo attachments (optional) */}
      {onFilesChange && (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t.photos} <span className="text-gray-400 text-[10px]">({t.optional})</span>
        </label>
        <div className="border border-dashed border-gray-300 rounded-md p-3 text-center">
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              onFilesChange([...selectedFiles, ...files]);
              e.target.value = ''; // Reset to allow re-selecting same files
            }}
            className="hidden"
            id="photo-upload"
          />
          <label htmlFor="photo-upload" className="cursor-pointer">
            <div className="text-gray-400 text-xl mb-1">📷</div>
            <p className="text-xs text-gray-500">{t.photosHint}</p>
          </label>
          {selectedFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {selectedFiles.map((file, i) => (
                <div key={`${file.name}-${i}`} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                  <span className="truncate mr-2">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => onFilesChange(selectedFiles.filter((_, idx) => idx !== i))}
                    className="text-red-400 hover:text-red-600 text-sm flex-shrink-0"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <p className="text-[10px] text-gray-400">{selectedFiles.length} {t.filesSelected}</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Consent checkboxes */}
      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.gdprConsent}
            onChange={(e) => handleChange('gdprConsent', e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className={`text-xs leading-tight ${errors.gdprConsent ? 'text-red-500' : 'text-gray-600'}`}>
            {t.gdprConsent}
          </span>
        </label>
        {errors.gdprConsent && <p className="text-red-500 text-[10px] ml-5">{errors.gdprConsent}</p>}

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.marketingConsent}
            onChange={(e) => handleChange('marketingConsent', e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-xs leading-tight text-gray-600">{t.marketingConsent}</span>
        </label>
      </div>
      </>
      )}
    </div>
  );
}

const translations = {
  es: {
    title: 'Tus datos',
    caseCategoryTitle: '¿Tu reparación es por…?',
    caseParticular: 'Particular',
    caseParticularDesc: 'Pago privado / presupuesto.',
    caseInsurance: 'Seguro (siniestro)',
    caseInsuranceDesc: 'Tramitación con aseguradora.',
    caseWarranty: 'Garantía',
    caseWarrantyDesc: 'Trabajo en garantía.',
    insuranceTitle: 'Datos del seguro',
    insuranceCompany: 'Aseguradora',
    insuranceCompanyPlaceholder: 'Mutua Madrileña, Mapfre…',
    insurancePolicyNumber: 'Nº de póliza',
    insurancePolicyNumberPlaceholder: 'Opcional',
    insuranceClaimNumber: 'Nº de siniestro',
    insuranceClaimNumberPlaceholder: 'Ej. 123456789',
    adjusterTitle: 'Contacto del perito (opcional)',
    adjusterName: 'Nombre del perito',
    adjusterNamePlaceholder: 'Ej. Ana López',
    adjusterPhone: 'Teléfono del perito',
    adjusterPhonePlaceholder: 'Ej. 600 000 000',
    adjusterEmail: 'Email del perito',
    adjusterEmailPlaceholder: 'perito@aseguradora.com',
    customerInfo: 'Datos de contacto',
    name: 'Nombre completo',
    namePlaceholder: 'Juan García',
    email: 'Email',
    emailPlaceholder: 'tu@email.com',
    phone: 'Teléfono',
    phonePlaceholder: '612 345 678',
    dni: 'DNI/NIE (opcional)',
    vehicleInfo: 'Datos del vehículo',
    registration: 'Matrícula',
    registrationPlaceholder: '1234 ABC',
    make: 'Marca',
    makePlaceholder: 'Seat',
    model: 'Modelo',
    modelPlaceholder: 'León',
    year: 'Año',
    optional: 'opcional',
    notes: 'Comentarios adicionales',
    notesPlaceholder: 'Describe brevemente el problema o servicio que necesitas...',
    photos: 'Fotos del vehículo',
    photosHint: 'Pulsa para añadir fotos del daño o vehículo',
    filesSelected: 'archivo(s) seleccionado(s)',
    gdprConsent:
      'Acepto la política de privacidad y el tratamiento de mis datos para gestionar mi cita.',
    marketingConsent:
      'Deseo recibir recordatorios y ofertas por email (puedes darte de baja en cualquier momento).',
  },
  en: {
    title: 'Your details',
    caseCategoryTitle: 'Is this repair for…?',
    caseParticular: 'Private',
    caseParticularDesc: 'Self-paid / quote.',
    caseInsurance: 'Insurance claim',
    caseInsuranceDesc: 'Handled with insurer.',
    caseWarranty: 'Warranty',
    caseWarrantyDesc: 'Warranty-covered work.',
    insuranceTitle: 'Insurance details',
    insuranceCompany: 'Insurance company',
    insuranceCompanyPlaceholder: 'e.g. Mapfre',
    insurancePolicyNumber: 'Policy number',
    insurancePolicyNumberPlaceholder: 'Optional',
    insuranceClaimNumber: 'Claim number',
    insuranceClaimNumberPlaceholder: 'e.g. 123456789',
    adjusterTitle: 'Adjuster contact (optional)',
    adjusterName: 'Adjuster name',
    adjusterNamePlaceholder: 'e.g. Jane Doe',
    adjusterPhone: 'Adjuster phone',
    adjusterPhonePlaceholder: 'e.g. +34 600 000 000',
    adjusterEmail: 'Adjuster email',
    adjusterEmailPlaceholder: 'adjuster@insurer.com',
    customerInfo: 'Contact information',
    name: 'Full name',
    namePlaceholder: 'John Smith',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone',
    phonePlaceholder: '612 345 678',
    dni: 'DNI/NIE (optional)',
    vehicleInfo: 'Vehicle information',
    registration: 'License plate',
    registrationPlaceholder: '1234 ABC',
    make: 'Make',
    makePlaceholder: 'Seat',
    model: 'Model',
    modelPlaceholder: 'León',
    year: 'Year',
    optional: 'optional',
    notes: 'Additional notes',
    notesPlaceholder: 'Briefly describe the problem or service you need...',
    photos: 'Vehicle photos',
    photosHint: 'Tap to add photos of the damage or vehicle',
    filesSelected: 'file(s) selected',
    gdprConsent:
      'I accept the privacy policy and the processing of my data to manage my appointment.',
    marketingConsent:
      'I want to receive reminders and offers by email (you can unsubscribe at any time).',
  },
};
